//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 21/10/2025
// Version       : 1
// Description   : Ver vencimietos de vehículos con su estado actual
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  Box,
  IconButton,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { getData } from "../../../services/library";
// ICONOS
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CircularProgress from "@mui/material/CircularProgress";

function VencimientosVehiculos({ vehiculo, handleCloseVencimientos }) {
  const [vencimientos, setVencimientos] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const obtenerVencimientos = () => {
    setError(null);
    setLoading(true);

    try {
      const url = "/vehiculos_vencimientos/" + vehiculo.uuid_vehiculo;

      const finishLoading = () => {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      };

      const posiblePromise = getData(url, (callbackData) => {
        setVencimientos(Array.isArray(callbackData) ? callbackData : []);
        finishLoading();
      });

      if (posiblePromise && typeof posiblePromise.then === "function") {
        posiblePromise
          .then((data) => {
            setVencimientos(Array.isArray(data) ? data : []);
          })
          .catch((err) => {
            console.error("Error getData (promise):", err);
            setError(err?.message || "Error al obtener vencimientos");
            setVencimientos([]);
          })
          .finally(() => {
            finishLoading();
          });
      }
    } catch (err) {
      console.error("Error en obtenerVencimientos:", err);
      setError(err?.message || "Error al obtener vencimientos");
      setVencimientos([]);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    obtenerVencimientos();
  }, []);

  // ************************************************************

  const formatDate = (iso) =>
    iso
      ? moment(iso).isValid()
        ? moment(iso).format("DD/MM/YYYY")
        : "-"
      : "-";

  const statusFor = (isoDate) => {
    if (!isoDate) return { label: "Sin fecha", color: "default" };
    const m = moment(isoDate).startOf("day");
    const today = moment().startOf("day");
    if (m.isBefore(today)) return { label: "Vencido", color: "error" };
    const diffDays = m.diff(today, "days");
    if (diffDays <= 30)
      return { label: `Próximo (${diffDays}d)`, color: "warning" };
    return { label: "Vigente", color: "success" };
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          backgroundColor: "#1976D2",
          py: 1,
          px: 2,
          mb: 2,
          color: "white",
        }}
      >
        {typeof handleCloseVencimientos === "function" ? (
          <IconButton
            onClick={handleCloseVencimientos}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        ) : (
          <Box width={40} />
        )}

        <Typography
          variant="h5"
          sx={{
            flexGrow: 1,
            textAlign: "center",
            fontWeight: 700,
            color: "white",
            letterSpacing: 0.5,
          }}
        >
          Vencimientos
        </Typography>

        {vehiculo?.patente ? (
          <Chip
            label={vehiculo.patente}
            size="small"
            sx={{
              fontWeight: 700,
              backgroundColor: "#e8f0ff",
              color: "#0b5fb8",
            }}
          />
        ) : (
          <Box width={60} />
        )}
      </Box>

      {error && (
        <Box sx={{ backgroundColor: "#fff6f6", borderRadius: 2, p: 1, mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Grid container spacing={2} sx={{ p: 1, mb: 2 }}>
        <Grid item xs={12}>
          <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            {loading ? (
              // Estado de carga - Muestra tarjeta con spinner
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                  <Card
                    elevation={2}
                    sx={{
                      borderRadius: 2,
                      height: "100%",
                      minHeight: 150,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      transition: "transform .16s ease, box-shadow .16s ease",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        backgroundColor: "#f5f5f5",
                        color: "#666",
                        py: 1,
                        textAlign: "center",
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        cursor: "default",
                        userSelect: "none",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          whiteSpace: "pre-line",
                          cursor: "default",
                        }}
                      >
                        Cargando...
                      </Typography>
                    </Box>

                    <CardContent
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        textAlign: "center",
                        px: 2,
                        py: 3,
                      }}
                    >
                      <CircularProgress size={40} />
                    </CardContent>

                    <Box
                      sx={{
                        height: 6,
                        borderRadius: "0 0 8px 8px",
                        background: "rgba(0,0,0,0.04)",
                      }}
                    />
                  </Card>
                </Grid>
              </Grid>
            ) : vencimientos && vencimientos.length > 0 ? (
              // Datos cargados = Muestra vencimientos
              <Grid container spacing={2}>
                {vencimientos.map((v) => {
                  const st = statusFor(v.vencimiento);
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={v.id}>
                      <Card
                        elevation={2}
                        sx={{
                          borderRadius: 2,
                          height: "100%",
                          minHeight: 150,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          transition:
                            "transform .16s ease, box-shadow .16s ease",
                          "&:hover": {
                            transform: "translateY(-6px)",
                            boxShadow: 6,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            backgroundColor: "#1976D2",
                            color: "#fff",
                            py: 1,
                            textAlign: "center",
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            cursor: "default",
                            userSelect: "none",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              whiteSpace: "pre-line",
                              cursor: "default",
                            }}
                          >
                            {v.componente}
                          </Typography>
                        </Box>

                        <CardContent
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            height: "100%",
                            textAlign: "center",
                            px: 2,
                            py: 1,
                          }}
                        >
                          <Box
                            sx={{
                              my: "auto",
                              cursor: "default",
                              userSelect: "none",
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {formatDate(v.vencimiento)}
                            </Typography>
                          </Box>
                          <Box sx={{ cursor: "default", userSelect: "none" }}>
                            <Chip
                              label={st.label}
                              size="small"
                              color={
                                st.color === "default" ? undefined : st.color
                              }
                              sx={{ fontWeight: 700, textTransform: "none" }}
                            />
                          </Box>
                        </CardContent>

                        <Box
                          sx={{
                            height: 6,
                            borderRadius: "0 0 8px 8px",
                            background:
                              st.color === "error"
                                ? "#d32f2f"
                                : st.color === "warning"
                                ? "#f57c00"
                                : st.color === "success"
                                ? "#2e7d32"
                                : "rgba(0,0,0,0.04)",
                          }}
                        />
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              // Sin datos
              <Box
                sx={{
                  textAlign: "center",
                }}
              >
                <Typography variant="subtitle1" sx={{ color: "#1976D2" }}>
                  <b>Sin vencimientos registrados</b>
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default VencimientosVehiculos;
